import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Category,
  Product,
  ShopSection,
  ShopSettings
} from '@infrab4a/connect';
import { CatalogService } from '@infrab4a/connect-angular';
import { MessageService } from 'primeng/api';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent
} from 'primeng/autocomplete';
import { CarouselResponsiveOptions } from 'primeng/carousel';
import { ShopSettingsService } from 'src/app/connect-api/api/shop-settings/shop-settings.service';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  MENSMARKET_HOME_DOCUMENT_ID,
  MENSMARKET_HOME_ROUTE
} from '../mens-new-home.constants';

export type Collection = {
  type: 'COLLECTION';
  fixed: boolean;
  editable: boolean;
  slug: string;
  title: string;
  categoryId: number;
  displayRules?: Array<Partial<DisplayRules>>;
  publishDate?: Date;
  expirationDate?: Date;
};

export type DisplayRules = {
  subscriberBuyer: boolean;
  notSubscriber: boolean;
  subscriberNotBuyer: boolean;
  topBadgeSubscriber: boolean;
};

@Component({
  selector: 'app-mens-new-home-collection-form',
  templateUrl: './collection-form.component.html',
  styleUrl: './collection-form.component.scss'
})
export class MensNewHomeCollectionFormComponent implements OnInit {
  @Input() id: string | null = null;
  form: FormGroup;
  private shopSettingsService = inject(ShopSettingsService);
  protected categoryService = inject(ShopCategoryService);
  private messageService = inject(MessageService);
  categories: Partial<Category>[];
  settings: Partial<ShopSettings> & { sections?: ShopSection[] };
  products: Product[];
  responsiveOptionsSmall: CarouselResponsiveOptions[] = [
    {
      breakpoint: '1199px',
      numVisible: 4,
      numScroll: 4
    },
    {
      breakpoint: '991px',
      numVisible: 3,
      numScroll: 3
    },
    {
      breakpoint: '767px',
      numVisible: 2,
      numScroll: 2
    }
  ];

  displayRuleOptions = [
    { label: 'Não se aplica', value: null },
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
  ];

  constructor(
    private fb: FormBuilder,
    readonly catalogService: CatalogService,
    readonly router: Router,
    readonly activatedRoute: ActivatedRoute
  ) {
    this.form = this.fb.group({
      id: [''],
      type: ['COLLECTION'],
      fixed: [false],
      editable: [true],
      slug: ['', Validators.required],
      title: ['', Validators.required],
      name: [''],
      categoryId: [null, Validators.required],
      displayRules: new FormGroup({
        subscriberBuyer: new FormControl(null),
        notSubscriber: new FormControl(null),
        subscriberNotBuyer: new FormControl(null),
        topBadgeSubscriber: new FormControl(null),
        beautyProfile: new FormControl(null)
      }),
      publishDate: [null],
      expirationDate: [null]
    });
  }

  async ngOnInit(): Promise<void> {
    this.settings = await this.shopSettingsService.getById(MENSMARKET_HOME_DOCUMENT_ID);
    this.id = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.id) {
      LoaderService.showLoader();
      const section: Collection = this.settings.sections?.find(
        (s) => s.id === this.id
      );

      if (section) {
        console.log('section', section);
        this.form.patchValue({ id: this.id, ...section });
        if (section.displayRules && Array.isArray(section.displayRules)) {
          for (const rule of section.displayRules) {
            for (const key in rule) {
              if (this.form.get('displayRules').get(key)) {
                this.form.get('displayRules').get(key).setValue(rule[key]);
              }
            }
          }
        }
        await this.getProducts(
          Category.toInstance({ id: section.categoryId } as any)
        );
      }
      LoaderService.showLoader(false);
    }

    console.log('this.settings', this.settings);
  }

  async searchCollections(event: AutoCompleteCompleteEvent): Promise<void> {
    try {
      const [categories, collections, brands] = await Promise.all([
        this.getCategories(event.query),
        this.getCollectionCategories(event.query),
        this.getBrandCategories(event.query)
      ]);
      this.categories = [...categories, ...collections, ...brands];
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async getCategories(input: string): Promise<Partial<Category>[]> {
    const { content } = await this.categoryService.getCategoriesList({
      filters: [
        {
          condition: 'contains',
          field: 'name',
          value: input,
          fieldType: 'text'
        },
        {
          condition: 'equals',
          field: 'isCollection',
          value: false,
          fieldType: 'boolean'
        },
        {
          condition: 'equals',
          field: 'published',
          value: true,
          fieldType: 'boolean'
        }
      ],
      sortBy: 'reference',
      sortDirection: 'asc',
      page: 0,
      pageSize: 10
    });

    return content;
  }

  async getCollectionCategories(input: string): Promise<Partial<Category>[]> {
    const { content } = await this.categoryService.getCategoriesList({
      filters: [
        {
          condition: 'contains',
          field: 'name',
          value: input,
          fieldType: 'text'
        },
        {
          condition: 'equals',
          field: 'isCollection',
          value: true,
          fieldType: 'boolean'
        },
        {
          condition: 'equals',
          field: 'isWishlist',
          value: false,
          fieldType: 'boolean'
        }
      ],
      page: 0,
      pageSize: 10
    });

    return content;
  }

  async getBrandCategories(input: string): Promise<Partial<Category>[]> {
    const { content } = await this.categoryService.getCategoriesList({
      filters: [
        {
          condition: 'contains',
          field: 'name',
          value: input,
          fieldType: 'text'
        },
        {
          condition: 'equals',
          field: 'brandCategory',
          value: true,
          fieldType: 'boolean'
        }
      ],
      page: 0,
      pageSize: 10
    });

    return content;
  }

  async collectionChanged($event?: AutoCompleteSelectEvent): Promise<void> {
    const category = $event?.value as Category;
    LoaderService.showLoader();
    if ($event.value) {
      this.form.controls['categoryId'].setValue(
        ($event.value as Partial<Category>).id
      );
      this.form.controls['slug'].setValue(
        ($event.value as Partial<Category>).slug
      );
      this.form.controls['title'].setValue(
        ($event.value as Partial<Category>).name
      );
    }
    try {
      await this.getProducts(category);
    } catch (error) {
      this.form.controls['categoryId'].reset();
      this.form.controls['slug'].reset();
      this.form.controls['title'].reset();
      delete this.products;
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async getProducts(category: Category): Promise<void> {
    const products = await this.catalogService
      .fetchProducts({
        category,
        perPage: 8,
        page: 0,
        sort: 'most-relevant'
      })
      .then((res) => res.products.data);

    if (!products.length) {
      throw { message: 'Produtos não encontrados na categoria' };
    }

    this.products = products;
  }

  async onSubmit(): Promise<void> {
    if (this.form.valid) {
      LoaderService.showLoader();
      const newSection = this.getNewSection();
      const sections = this.settings.sections || [];
      const existingIndex = sections.findIndex((s) => s.id === this.id);

      if (existingIndex >= 0) {
        sections[existingIndex] = newSection;
      } else {
        sections.splice(sections.length - 1, 0, newSection);
      }

      await this.shopSettingsService.update({
        id: this.settings.id,
        sections: sections
      });
    }
    LoaderService.showLoader(false);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Coleção salva com sucesso'
    });

    this.router.navigate([MENSMARKET_HOME_ROUTE]);
  }

  private getNewSection(): Collection {
    const formValue = this.form.value;
    const displayRules = [];
    const rules = formValue.displayRules;
    for (const key in rules) {
      if (rules[key] !== null) {
        displayRules.push({ [key]: rules[key] });
      }
    }
    const collection = {
      id: formValue.id || crypto.randomUUID(),
      fixed: formValue.fixed,
      editable: formValue.editable,
      type: formValue.type,
      slug: formValue.slug,
      title: formValue.title,
      categoryId: formValue.categoryId,
      displayRules: displayRules.length > 0 ? displayRules : [],
      publishDate: formValue.publishDate,
      expirationDate: formValue.expirationDate
    };

    return collection;
  }
}
